import React from 'react';
import { CircuitRenderer, CircuitElement } from './CircuitRenderer';

interface ResistorDiagramProps {
    isSeries: boolean;
    resistors: number[];
    equivalent?: number;
}

// Standardized diagram dimensions
const DIAGRAM_WIDTH = 450;
const DIAGRAM_HEIGHT = 300;

export const ResistorDiagram: React.FC<ResistorDiagramProps> = ({
    isSeries,
    resistors,
    equivalent: _equivalent
}) => {
    // _equivalent available for future use (e.g., showing Req label)
    const width = DIAGRAM_WIDTH;
    const height = DIAGRAM_HEIGHT;
    const centerY = height / 2;
    const startX = 60;
    const endX = width - 60;

    let elements: CircuitElement[] = [];
    const junctions: { x: number; y: number; label?: string; labelPosition?: 'left' | 'right' }[] = [];

    if (isSeries) {
        // Series: A ---- R1 ---- R2 ---- R3 ---- B
        const count = resistors.length;
        const spacing = (endX - startX) / count;

        resistors.forEach((r, i) => {
            const slotStart = startX + (i * spacing);
            const slotEnd = startX + ((i + 1) * spacing);

            elements.push({
                type: 'resistor',
                from: { x: slotStart, y: centerY },
                to: { x: slotEnd, y: centerY },
                value: r,
                label: `R${i + 1}=${r}Ω`
            });
        });

        // Add terminal junctions with labels
        junctions.push({ x: startX, y: centerY, label: 'A', labelPosition: 'left' });
        junctions.push({ x: endX, y: centerY, label: 'B', labelPosition: 'right' });

    } else {
        // Parallel
        const count = resistors.length;
        const verticalSpacing = 60;
        const busLeftX = startX + 40;
        const busRightX = endX - 40;
        const topY = centerY - ((count - 1) * verticalSpacing) / 2;
        const bottomY = centerY + ((count - 1) * verticalSpacing) / 2;

        // Main inputs
        elements.push({ type: 'wire', from: { x: startX, y: centerY }, to: { x: busLeftX, y: centerY } });
        elements.push({ type: 'wire', from: { x: busRightX, y: centerY }, to: { x: endX, y: centerY } });

        // Bus Bars
        elements.push({ type: 'wire', from: { x: busLeftX, y: topY }, to: { x: busLeftX, y: bottomY } });
        elements.push({ type: 'wire', from: { x: busRightX, y: topY }, to: { x: busRightX, y: bottomY } });

        resistors.forEach((r, i) => {
            const y = topY + i * verticalSpacing;
            elements.push({
                type: 'resistor',
                from: { x: busLeftX, y: y },
                to: { x: busRightX, y: y },
                value: r,
                label: `R${i + 1} = ${r}Ω`
            });
        });

        // Add terminal junctions with labels
        junctions.push({ x: startX, y: centerY, label: 'A', labelPosition: 'left' });
        junctions.push({ x: endX, y: centerY, label: 'B', labelPosition: 'right' });
    }

    return (
        <CircuitRenderer
            width={width}
            height={height}
            elements={elements}
            junctions={junctions}
        />
    );
};
